import type { EntityAdminRecord } from '@src/components/EntityAdmin/types';
import { Input } from '@src/components/Form/Input/Input';
import { NumberInput } from '@src/components/Form/NumberInput/NumberInput';
import { get } from 'lodash-es';
import { memo, useCallback, useMemo } from 'react';
import type { InputDrawerOption, InputNumericDrawerOption } from '../types';
import { drawerContentMemoComparison, type DrawerContentProps } from './utils';

const InputContentInner = <TDrawerRecord extends EntityAdminRecord>({
  drawerOption,
  form,
  isEditing,
  onFormUpdate,
  inheritedValue,
}: DrawerContentProps<InputDrawerOption<TDrawerRecord> | InputNumericDrawerOption<TDrawerRecord>, TDrawerRecord>) => {
  const isNumericInput = drawerOption.type === 'inputNumeric';

  const variant = isNumericInput ? (drawerOption as InputNumericDrawerOption<TDrawerRecord>).variant : undefined;

  const suffix = variant === 'bps' ? 'BPS' : variant === 'percent' ? '%' : drawerOption.getInputSuffix?.(form);

  const prefix = useMemo(() => drawerOption.getInputPrefix?.(form), [drawerOption, form]);

  const latestValue = get(form, drawerOption.field) as string | undefined;

  const inputValue = useMemo(() => {
    if (!latestValue) {
      return '';
    }
    return String(latestValue);
  }, [latestValue]);

  const disabled = useMemo(() => {
    return (drawerOption.disabledWhenEditing && isEditing) || drawerOption.getIsDisabled?.(form);
  }, [drawerOption, isEditing, form]);

  const handleChange = useCallback(
    (value: string) => {
      onFormUpdate(drawerOption.field, value);
      drawerOption.onChange?.(value, onFormUpdate, form);
    },
    [drawerOption, form, onFormUpdate]
  );

  if (isNumericInput) {
    const numberInputProps = (drawerOption as InputNumericDrawerOption<TDrawerRecord>).numberInputProps;
    return (
      <NumberInput
        value={inputValue}
        onChange={handleChange}
        disabled={disabled}
        placeholder={inheritedValue ?? drawerOption.placeholder}
        prefix={prefix}
        suffix={suffix}
        {...numberInputProps}
        data-testid={`entity-admin-drawer-${String(drawerOption.field)}`}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
      />
    );
  }

  return (
    <Input
      value={inputValue ?? ''}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)}
      disabled={disabled}
      placeholder={inheritedValue ?? drawerOption.placeholder}
      prefix={prefix}
      suffix={suffix}
      inputType="text"
      data-testid={`entity-admin-drawer-${String(drawerOption.field)}`}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck="false"
    />
  );
};

export const InputContent = memo(InputContentInner, drawerContentMemoComparison) as <
  TDrawerRecord extends EntityAdminRecord,
>(
  props: DrawerContentProps<InputDrawerOption<TDrawerRecord> | InputNumericDrawerOption<TDrawerRecord>, TDrawerRecord>
) => JSX.Element;
